import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, Wallet, Package, Percent, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, num, fmtDate } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const STATUS_TONE = {
  UNPAID: "bg-rose-50 text-rose-700 border-rose-200",
  PARTIALLY_PAID: "bg-amber-50 text-amber-700 border-amber-200",
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  OVERDUE: "bg-rose-100 text-rose-800 border-rose-300",
  CANCELLED: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function PurchaseBillView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const [bill, setBill] = useState(null);

  const load = useCallback(() => {
    api.get(`/purchases/bills/${id}`).then(({ data }) => setBill(data))
      .catch((e) => toast.error(apiError(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!bill) return <div className="text-sm text-slate-500">Loading bill…</div>;

  const t = bill.totals || {};
  const isIntra = bill.supply_type === "INTRA_STATE";

  const cancel = async () => {
    const reason = window.prompt(`Cancel bill ${bill.supplier_bill_number}?`, "Entered in error");
    if (!reason) return;
    try {
      await api.post(`/purchases/bills/${id}/cancel`, { reason });
      toast.success("Bill cancelled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="bill-view-page">
      <div>
        <Link to="/purchases" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft size={13} /> All purchases
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight font-mono"
                data-testid="bill-number">{bill.supplier_bill_number}</h1>
              <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold uppercase ${
                STATUS_TONE[bill.payment_status] || STATUS_TONE.UNPAID}`}>
                {bill.payment_status?.replace("_", " ")}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              <Link to={`/parties/${bill.supplier_id}`} className="text-blue-700 hover:text-blue-900">
                {bill.supplier_name}
              </Link>
              {" · "}{fmtDate(bill.bill_date)}
              {bill.due_date && <> · due {fmtDate(bill.due_date)}</>}
              {" · "}<span className="font-mono">{inr(t.grand_total)}</span>
              {bill.balance_due > 0.01 && (
                <> · balance <span className="font-mono text-rose-700">{inr(bill.balance_due)}</span></>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && bill.balance_due > 0.01 && bill.status === "RECORDED" && (
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="bill-pay"
                onClick={() => navigate(`/payments?new=1&type=PAYMENT_OUT&party_id=${bill.supplier_id}&amount=${bill.balance_due}`)}>
                <Wallet size={15} /> Record payment
              </Button>
            )}
            {canEdit && bill.status === "RECORDED" && bill.amount_paid < 0.01 && (
              <Button size="sm" variant="outline" onClick={cancel} data-testid="bill-cancel-btn"
                className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50">
                <Ban size={15} /> Cancel
              </Button>
            )}
          </div>
        </div>
      </div>

      {bill.status === "CANCELLED" && (
        <div className="rounded-md border border-slate-300 bg-slate-100 px-4 py-2.5 text-xs text-slate-600">
          <strong>Cancelled.</strong> {bill.cancel_reason} — it no longer counts toward payables,
          the purchase register or ITC.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Supplier</div>
          <div className="mt-1 font-semibold">{bill.supplier_name}</div>
          {bill.supplier_gstin && (
            <div className="font-mono text-[11px] text-slate-500">{bill.supplier_gstin}</div>
          )}
          <div className="mt-2 text-[11px] text-slate-500">
            Place of supply: {bill.place_of_supply_name} · {isIntra ? "Intra-state" : "Inter-state"}
            {bill.is_reverse_charge && " · Reverse charge"}
          </div>
        </div>

        <div className={`rounded-md border p-4 ${bill.itc_eligible
          ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
          data-testid="bill-itc-card">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">
            <Percent size={12} /> Input tax credit
          </div>
          {bill.itc_eligible ? (
            <>
              <div className="mt-1 font-heading text-xl font-bold tabular-nums text-emerald-800">
                {inr(bill.input_tax_credit)}
              </div>
              <div className="mt-0.5 text-[11px] text-emerald-700">Claimable against output tax</div>
            </>
          ) : (
            <>
              <div className="mt-1 font-heading text-xl font-bold tabular-nums text-slate-500">
                {inr(bill.tax_as_cost)}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                Not claimable{bill.itc_reason ? ` — ${bill.itc_reason}` : ""}. Treated as cost.
              </div>
            </>
          )}
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">
            <Package size={12} /> Stock
          </div>
          <div className="mt-1 text-sm">
            {bill.stock_receipt_id
              ? <span className="text-emerald-700">Received into inventory</span>
              : <span className="text-slate-500">Not received into inventory</span>}
          </div>
          {bill.notes && <div className="mt-2 text-[11px] text-slate-500">{bill.notes}</div>}
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="bill-lines">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">#</th>
              <th className="px-3 py-2.5 text-left font-semibold">Description</th>
              <th className="px-3 py-2.5 text-left font-semibold">HSN/SAC</th>
              <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
              <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Taxable</th>
              {isIntra ? (
                <>
                  <th className="px-3 py-2.5 text-right font-semibold">CGST</th>
                  <th className="px-3 py-2.5 text-right font-semibold">SGST</th>
                </>
              ) : <th className="px-3 py-2.5 text-right font-semibold">IGST</th>}
              <th className="px-4 py-2.5 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(bill.lines || []).map((l) => (
              <tr key={l.line_number} className="tabular-nums">
                <td className="px-4 py-2.5 text-slate-500">{l.line_number}</td>
                <td className="px-3 py-2.5 font-medium">{l.item_name}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{l.hsn_sac_code}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">
                  {num(l.quantity, 3)} <span className="text-slate-400">{l.unit_symbol}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(l.rate)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(l.taxable_value)}</td>
                {isIntra ? (
                  <>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(l.cgst_amount)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(l.sgst_amount)}</td>
                  </>
                ) : <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(l.igst_amount)}</td>}
                <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white">
          {[["Taxable value", t.taxable_value],
            isIntra ? ["CGST", t.cgst_amount] : null,
            isIntra ? ["SGST", t.sgst_amount] : null,
            !isIntra ? ["IGST", t.igst_amount] : null,
            Number(t.cess_amount) ? ["Cess", t.cess_amount] : null,
            Number(t.round_off) ? ["Round off", t.round_off] : null,
          ].filter(Boolean).map(([label, value]) => (
            <div key={label} className="flex justify-between border-b border-slate-100 px-4 py-2 text-sm">
              <span className="text-slate-600">{label}</span>
              <span className="font-mono tabular-nums">{inr(value)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
            <span className="text-xs font-semibold uppercase tracking-widest">Bill total</span>
            <span className="font-mono text-lg font-bold" data-testid="bill-total">{inr(t.grand_total)}</span>
          </div>
          {bill.amount_paid > 0 && (
            <>
              <div className="flex justify-between px-4 py-2 text-sm">
                <span className="text-slate-600">Paid</span>
                <span className="font-mono text-emerald-700">{inr(bill.amount_paid)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-100 px-4 py-2 text-sm font-semibold">
                <span>Balance</span>
                <span className="font-mono text-rose-700">{inr(bill.balance_due)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {!!bill.payments?.length && (
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">Payments made</div>
          <table className="w-full text-xs" data-testid="bill-payments">
            <tbody className="divide-y divide-slate-100">
              {bill.payments.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2.5 font-mono">{p.payment_number}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(p.payment_date)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{p.payment_mode?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {inr((p.allocations || []).filter((a) => a.bill_id === id)
                      .reduce((s, a) => s + a.amount, 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
